import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Plus, Sparkles, ShieldCheck, X } from "lucide-react";

const QUICK_PACKS = [
  { label: "+1k AI",       ai: 1000,  voice: 0,   scrapes: 0 },
  { label: "+5k AI",       ai: 5000,  voice: 0,   scrapes: 0 },
  { label: "+100 Voice",   ai: 0,     voice: 100, scrapes: 0 },
  { label: "+500 Voice",   ai: 0,     voice: 500, scrapes: 0 },
  { label: "+50 Scrapes",  ai: 0,     voice: 0,   scrapes: 50 },
  { label: "Stack 10k/1k/100", ai: 10000, voice: 1000, scrapes: 100 },
];

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [modal, setModal] = useState(null); // {tenant, ai, voice, scrapes, mode}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/tenants").then((r) => setTenants(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, s) => {
    await api.post(`/admin/tenants/${id}/status`, { subscription_status: s });
    toast.success("Status updated"); load();
  };

  const openTopUp = (t) => setModal({ tenant: t, ai: 5000, voice: 500, scrapes: 50, agent_limit: 0, mode: "increment" });

  const submitTopUp = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${modal.tenant.id}/credits`, {
        ai_credits: parseInt(modal.ai, 10) || 0,
        voice_credits: parseInt(modal.voice, 10) || 0,
        extra_scrapes: parseInt(modal.scrapes, 10) || 0,
        agent_limit: parseInt(modal.agent_limit, 10) || 0,
        mode: modal.mode,
      });
      toast.success(`${modal.mode === "set" ? "Set" : "Topped up"} ${modal.tenant.name}`);
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update credits");
    } finally {
      setBusy(false);
    }
  };

  const applyQuick = async (t, pack) => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${t.id}/credits`, {
        ai_credits: pack.ai, voice_credits: pack.voice, extra_scrapes: pack.scrapes, mode: "increment",
      });
      toast.success(`${pack.label} → ${t.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not top up");
    } finally {
      setBusy(false);
    }
  };

  const isSystem = (t) => t.type === "system" || t.name === "System";

  return (
    <div data-testid="admin-tenants-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-display font-extrabold text-3xl">Tenants &amp; credits</h1>
        <p className="text-sm text-[#4B5563]">Top up credits for any tenant — including the System tenant powering the public chat bubble.</p>
      </div>

      <div className="hs-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
            <tr>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">AI credits</th>
              <th className="text-right px-4 py-3">Voice credits</th>
              <th className="text-right px-4 py-3">Scrapes</th>
              <th className="text-right px-4 py-3">Users</th>
              <th className="text-right px-4 py-3">Agents</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} className={`border-t border-[#E2E8F0] hover:bg-[#F9FAFB] ${isSystem(t) ? "bg-[#F0F4FF]/40" : ""}`} data-testid={`tenant-row-${t.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium flex items-center gap-2">
                    {t.name}
                    {isSystem(t) && (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#0044FF]/12 text-[#0044FF] inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> system · yours
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-[#4B5563]">{t.industry || "—"} · {t.package_id || "no plan"}</div>
                </td>
                <td className="px-4 py-3">
                  <select value={t.subscription_status || "trial"} onChange={(e) => setStatus(t.id, e.target.value)} data-testid={`tenant-status-${t.id}`} className="border border-[#E2E8F0] px-2 py-1 rounded-sm text-xs bg-white">
                    {["trial", "active", "suspended", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className={`px-4 py-3 text-right font-mono ${t.ai_credits <= 0 ? "text-[#EF4444] font-bold" : ""}`}>{t.ai_credits}</td>
                <td className={`px-4 py-3 text-right font-mono ${t.voice_credits <= 0 ? "text-[#EF4444] font-bold" : ""}`}>{t.voice_credits}</td>
                <td className="px-4 py-3 text-right font-mono">{t.extra_scrapes}</td>
                <td className="px-4 py-3 text-right">{t.users}</td>
                <td className="px-4 py-3 text-right">{t.agents}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openTopUp(t)}
                    disabled={busy}
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] bg-[#0044FF] text-white hover:bg-[#0033CC] font-medium"
                    data-testid={`tenant-topup-${t.id}`}
                  >
                    <Coins className="w-3.5 h-3.5" /> Top up
                  </button>
                </td>
              </tr>
            ))}
            {tenants.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#4B5563]">No tenants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* TOP-UP MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModal(null)} data-testid="topup-modal">
          <div className="bg-white rounded-[6px] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#0044FF]">{modal.mode === "set" ? "set balance" : "add credits"}</div>
                <div className="font-display font-bold text-lg mt-1">Top up — {modal.tenant.name}</div>
              </div>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-[#F3F4F6] rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2">
                {["increment", "set"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setModal({ ...modal, mode: m })}
                    className={`px-3 py-1.5 rounded-[4px] text-xs font-medium ${modal.mode === m ? "bg-[#0F1115] text-white" : "bg-[#F3F4F6] text-[#4B5563]"}`}
                  >
                    {m === "increment" ? "+ Add to current" : "= Set exact"}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-4 gap-3">
                <Field label="AI credits"    value={modal.ai}      onChange={(v) => setModal({ ...modal, ai: v })}      hint={`now ${modal.tenant.ai_credits}`} />
                <Field label="Voice credits" value={modal.voice}   onChange={(v) => setModal({ ...modal, voice: v })}   hint={`now ${modal.tenant.voice_credits}`} />
                <Field label="Scrapes"       value={modal.scrapes} onChange={(v) => setModal({ ...modal, scrapes: v })} hint={`now ${modal.tenant.extra_scrapes}`} />
                <Field label="Agent limit"   value={modal.agent_limit} onChange={(v) => setModal({ ...modal, agent_limit: v })} hint={`now ${modal.tenant.agent_limit ?? 0}`} testid="topup-agent-limit" />
              </div>
              <div className="text-[11px] font-mono text-[#4B5563] -mt-1">
                Agent limit sets the max number of AI agents this tenant can create. In &quot;Add to current&quot; mode, leave at 0 to keep the current cap.
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#4B5563] mb-2">Quick packs</div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PACKS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => setModal({ ...modal, ai: p.ai, voice: p.voice, scrapes: p.scrapes, mode: "increment" })}
                      className="px-3 py-1.5 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] text-xs font-medium"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-[#E2E8F0] flex items-center justify-between gap-3">
              <div className="text-[11px] font-mono text-[#4B5563]">
                {modal.mode === "set" ? "Replaces the current balance exactly." : "Adds to whatever is on the account."}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="btn-outline text-sm">Cancel</button>
                <button onClick={submitTopUp} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="topup-submit">
                  {busy ? "Saving…" : (<><Sparkles className="w-4 h-4" /> Apply</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, hint, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="hs-input mt-1.5 font-mono"
        data-testid={testid || `topup-${label.toLowerCase().replace(/\s+/g, "-")}`}
      />
      <span className="text-[10px] font-mono text-[#4B5563] mt-1 block">{hint}</span>
    </label>
  );
}
